'use client';

import { useEffect, useState } from 'react';

interface WaveAnimationProps {
  waveCount?: number;
  height?: number;
  opacity?: number;
}

/**
 * @description Continuous wave animation component
 * Creates multiple animated waves at the bottom of a container
 */
export const WaveAnimation = ({
  waveCount = 3,
  height = 120,
  opacity = 0.3,
}: WaveAnimationProps) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Mark as mounted to prevent hydration mismatch
    setIsMounted(true);

    // Check for dark mode
    const checkDarkMode = () => {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const hasNativeDark = document.documentElement.hasAttribute('native-dark-active');
      setIsDarkMode(prefersDark || hasNativeDark);
    };

    // Check on mount
    checkDarkMode();

    // Listen for changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => checkDarkMode();
    mediaQuery.addEventListener('change', handleChange);

    // Also check for native-dark-active attribute changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['native-dark-active'],
    });

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      observer.disconnect();
    };
  }, []);

  // Generate seamless wave paths with significant height variation
  // Using a period that divides evenly into the width for seamless looping
  const generateWavePath = (index: number) => {
    // More varied amplitudes - each wave is significantly different
    const amplitudes = [25, 40, 15, 35, 20]; // Different heights for each wave
    const frequencies = [0.015, 0.025, 0.01, 0.02, 0.018]; // Different frequencies
    const verticalOffsets = [0, -10, 5, -15, 8]; // Vertical positioning

    // For the farthest wave (highest index), make it less wavy
    const isFarthest = index === waveCount - 1;
    let amplitude = amplitudes[index] || 20;
    let frequency = frequencies[index] || 0.015;

    if (isFarthest) {
      // Reduce amplitude and frequency for the farthest wave to make it less wavy
      amplitude *= 0.5; // Half the amplitude
      frequency *= 0.6; // Lower frequency for longer, gentler waves
    }

    const verticalOffset = verticalOffsets[index] || 0;

    // Calculate period for seamless looping
    const period = (2 * Math.PI) / frequency;
    // Use a fixed width that's exactly one or more complete periods for perfect looping
    const periodsPerWave = 2; // Number of complete periods per wave pattern
    const waveWidth = period * periodsPerWave;

    const centerY = height / 2 + verticalOffset;
    let path = `M0,${centerY}`;

    // Generate points for complete wave periods
    // Ensure the last point matches the first point for seamless loop
    const steps = 200; // Higher resolution for smoother curves
    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * waveWidth;
      const y = centerY + Math.sin(x * frequency) * amplitude;
      path += ` L${x},${y}`;
    }

    // Close the path to fill the bottom
    path += ` L${waveWidth},${height} L0,${height} Z`;
    return { path, width: waveWidth };
  };

  // Theme-aware color shades
  // Light mode: dark ink colors (from darkest to lightest)
  // Dark mode: light ink colors (from lightest to darkest for depth effect)
  const lightModeColors = ['#1E2022', '#677788', '#97A4AF', '#B8C1CA', '#D1D8DE'];
  const darkModeColors = ['#F8F9FA', '#BDC5D1', '#97A4AF', '#6B7280', '#4B5563'];
  const waveColors = isDarkMode ? darkModeColors : lightModeColors;

  // Don't render waves on server to prevent hydration mismatch
  // Render a placeholder div with the same dimensions
  if (!isMounted) {
    return (
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden"
        style={{ height: `${height}px` }}
      />
    );
  }

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden"
      style={{ height: `${height}px` }}
    >
      {Array.from({ length: waveCount }).map((_, index) => {
        const speed = 20 + (index * 5); // Different speeds for each wave
        const delay = index * 0.5; // Staggered delays
        const waveOpacity = opacity * (1 - index * 0.15); // Decreasing opacity for depth
        // Use different shades for each wave layer
        const waveColor = waveColors[index] || waveColors[waveColors.length - 1];

        const { path, width } = generateWavePath(index);
        const waveKey = `wave-${width}-${speed}-${waveOpacity}`;
        // Create a unique animation name for each wave to allow different translation distances
        const animationName = `wave-${index}`;

        return (
          <div
            key={waveKey}
            className="absolute inset-x-0 bottom-0"
            style={{
              height: `${height}px`,
              opacity: waveOpacity,
            }}
          >
            {/* Duplicate the wave pattern multiple times for seamless looping */}
            <svg
              className="absolute bottom-0 left-0 h-full"
              preserveAspectRatio="none"
              viewBox={`0 0 ${width * 4} ${height}`}
              xmlns="http://www.w3.org/2000/svg"
              style={{
                width: `${width * 4}px`,
                animation: `${animationName} ${speed}s linear infinite`,
                animationDelay: `${delay}s`,
              }}
            >
              {/* Render multiple copies of the wave for seamless transition - more copies to prevent gaps */}
              {[0, 1, 2, 3].map((copy) => (
                <path
                  key={copy}
                  d={path}
                  fill={waveColor}
                  transform={`translate(${copy * width}, 0)`}
                />
              ))}
            </svg>
            <style jsx>
              {`
                @keyframes ${animationName} {
                  0% {
                    transform: translateX(-${width}px);
                  }
                  100% {
                    transform: translateX(0);
                  }
                }
              `}
            </style>
          </div>
        );
      })}
    </div>
  );
};

